import * as L from 'leaflet';
import * as _ from 'lodash-es';
import { ActiveToast } from 'ngx-toastr';
import { BehaviorSubject, defer, identity, Observable, Subject } from 'rxjs';
import { concatMap, distinctUntilChanged, tap } from 'rxjs/operators';
import { MessagingService } from 'src/app/core/messaging/messaging.service';
import { TranslationService } from 'src/app/core/translation/translation.service';
import { GspLoggerService } from 'src/app/log-handler.service';
import { Feature } from 'src/app/shared/map-data-services/feature/feature';
import { FeatureService } from 'src/app/shared/map-data-services/feature/feature.service';
import { Layer } from 'src/app/shared/map-data-services/layer/layer';

import { ProjectStreamService } from '../current-project/project-stream.service';
import { FeatureFilter } from '../feature-filter/feature-filter';
import { FullFeatureFilter } from '../feature-filter/full-feature-filter';
import { LegacyTask } from '../legacy-task/legacy-task';
import { Task } from '../task/task';
import { ArrayUtils } from '../utility/array-utils';
import { CloneUtils } from '../utility/clone-utils';
import { StringUtils } from '../utility/string-utils';
import {
    GeometryTypeSelectionCriteria,
    JobSelectionCriteria,
    SelectionCriteria,
    TaskSelectionCriteria,
    TodoSelectionCriteria
} from './selection-criteria';

/**
 * A FeatureSet is used to managed a set of features; e.g. selected features, visible features, etc.
 *
 */
export class FeatureSet {
    // a subject and derived stream for the current feature set
    currentFeaturesStream = new BehaviorSubject<Feature[]>([]);

    featureFilter = new FeatureFilter();

    currentFeatures: Feature[] = [];
    currentFilterLayers: Layer[] = [];
    activeToast: ActiveToast<any> = null;
    selectedFeatureIds: string[] = [];

    // Track if current operation should be cancelled.
    // True when: Feature panel closed or selection changed while loading paged features.
    private isCancelled = false;

    // Counter to track which operation is currently active.
    // Each new operation gets an incremented ID. Helps stop older query queue.
    private currentOperationId = 0;

    private isLoadingFeatures$ = new BehaviorSubject<boolean>(false);

    // The actions queue takes a deferred observable which contains a method that returns a promise.
    // It ensures that each action is executed before the next one is started, as the
    // the result (i.e. currentFeatures and currentFilterLayers) must feed from one action to the next.
    actionsQueue = new Subject<Observable<Feature[]>>();

    constructor(
        private messaging: MessagingService,
        private projectStream: ProjectStreamService,
        private featureService: FeatureService,
        private name: string, // To help differentiate instances while debugging
        private hideNotifications: boolean,
        private translate: TranslationService,
        private logger: GspLoggerService
    ) {
        // Debugging - change to true for logging.
        const debugging = false;
        if (debugging) {
            this.currentFeaturesStream.subscribe(features => {
                this.logger.debug('this._current = ' + this.name + ' ' + features.length + ' features');
            });
        }

        this.actionsQueue
            .pipe(
                concatMap(identity),
                distinctUntilChanged(_.isEqual),
                tap((features: Feature[]) => {
                    this.currentFeatures = features;
                })
            )
            .subscribe(features => {
                this.currentFeaturesStream.next(features);
            });
        // above subscribe re-broadcasts via the subject ref: https://medium.com/@luukgruijs/understanding-rxjs-subjects-339428a1815b
    }

    public get isLoadingFeaturesStream(): BehaviorSubject<boolean> {
        return this.isLoadingFeatures$;
    }

    // currently selected features must be in available layers (= map workspace layers)
    public modifyAvailableLayers(layers: Layer[]): void {
        const modifyAvailableLayersAction = () => {
            const availableLayerIds = layers.map(layer => layer.id);
            const availableFeatures = this.currentFeatures.filter(
                feature => availableLayerIds.indexOf(feature.layerId) > -1
            );

            return Promise.resolve(availableFeatures);
        };

        this.actionsQueue.next(defer(modifyAvailableLayersAction));
    }

    // set layers filter (note: we no longer remove features which are in non-visible layers)
    public modifyFilterLayers(layers: Layer[]): void {
        const modifyFilterLayersAction = () => {
            this.currentFilterLayers = layers;
            // Added for fixing bug for updating selected feature color on layerColor change
            const layerColorsObj: { [key: string]: string } = {};
            layers.forEach(layer => {
                layerColorsObj[layer.id] = layer.color;
            });
            const currentFeatures = this.currentFeatures.map(feature => {
                const featureCopy = _.cloneDeep(feature);
                featureCopy.colorKey = layerColorsObj[featureCopy.layerId];
                return featureCopy;
            });
            return Promise.resolve(currentFeatures);
        };
        this.actionsQueue.next(defer(modifyFilterLayersAction));
    }

    // replace selection
    public replace(selectionCriteria: SelectionCriteria, callBack?: (features: Feature[]) => void): void {
        // Cancel ongoing queries if any.
        this.isCancelled = true;
        this.currentOperationId++;

        this.actionsQueue.complete();
        this.actionsQueue = new Subject<Observable<Feature[]>>();

        this.actionsQueue
            .pipe(
                concatMap(identity),
                distinctUntilChanged(_.isEqual),
                tap((features: Feature[]) => {
                    this.currentFeatures = features;
                })
            )
            .subscribe(features => {
                this.currentFeaturesStream.next(features);
            });

        const replaceFeaturesAction = (): Promise<Feature[]> => {
            const operationId = this.currentOperationId;
            this.isCancelled = false;

            return this.getFeatures(selectionCriteria, this.currentFilterLayers, operationId).then(newFeatures => {
                // Don't process results if operation was superseded
                if (operationId !== this.currentOperationId) {
                    return null;
                }

                if (callBack) {
                    callBack(newFeatures);
                }

                return newFeatures;
            });
        };
        this.actionsQueue.next(defer(replaceFeaturesAction));
    }

    // add to selection
    public add(
        selectionCriteria: SelectionCriteria,
        callBack?: (features: Feature[], selectionCriteriaFeatures: Feature[]) => void
    ): void {
        const addFeaturesAction = (): Promise<any> => {
            // Capture existing features at execution time to preserve them during queued operations
            const existingFeatures = [...this.currentFeatures];

            let selectionCriteriaFeatures: Feature[] = [];
            return this.getFeatures(selectionCriteria, this.currentFilterLayers, this.currentOperationId)
                .then(
                    newFeatures => {
                        selectionCriteriaFeatures = newFeatures;
                        // The result of add is the existing features with the new selected ones merged
                        const selectedFeatures = CloneUtils.cloneDeep(existingFeatures);
                        const selectedFeaturesIds = selectedFeatures.map(feature => feature.id);

                        newFeatures.forEach(newFeature => {
                            const index = selectedFeaturesIds.indexOf(newFeature.id);
                            if (index >= 0) {
                                selectedFeatures[index] = newFeature;
                            } else {
                                selectedFeatures.push(newFeature);
                            }
                        });

                        return selectedFeatures;
                    },
                    (): Feature[] =>
                        // On error return existing features
                        existingFeatures
                )
                .then(features => {
                    if (callBack) {
                        callBack(features, selectionCriteriaFeatures);
                    }
                    return features;
                });
        };
        this.actionsQueue.next(defer(addFeaturesAction));
    }

    // remove from selection
    public remove(
        selectionCriteria: SelectionCriteria,
        callBack?: (features: Feature[], selectionCriteriaFeatureIds: string[]) => void
    ): void {
        const removeFeaturesAction = (): Promise<Feature[]> => {
            const currentSelectedFeatures = [...this.currentFeatures];

            let selectionCriteriaFeatureIds: string[] = [];
            return this.getFeatureIds(selectionCriteria, this.currentFilterLayers, this.currentOperationId)
                .then(doomedIds => {
                    selectionCriteriaFeatureIds = doomedIds;
                    const remainingFeatures = currentSelectedFeatures.filter(f => doomedIds.indexOf(f.id) === -1);
                    return remainingFeatures;
                })
                .then((features: Feature[]) => {
                    if (callBack) {
                        callBack(features, selectionCriteriaFeatureIds);
                    }
                    return features;
                });
        };
        this.actionsQueue.next(defer(removeFeaturesAction));
    }

    public modifyFilters(newFeatureFilter: FeatureFilter): void {
        this.featureFilter = newFeatureFilter;
    }

    public modifyTasks(tasks: Task[] | LegacyTask[]): void {
        this.selectedFeatureIds = [];
        tasks.forEach(task => {
            task.features = task.features || [];
            const featureIds = task.features.map(feature => feature.featureId);
            this.selectedFeatureIds = this.selectedFeatureIds.concat(featureIds);
        });
    }

    private getFeatures(
        selectionCriteria: SelectionCriteria,
        filterLayers: Layer[],
        operationId: number
    ): Promise<Feature[]> {
        return this.getFeaturesOrFeatureIds(selectionCriteria, filterLayers, false, operationId) as Promise<Feature[]>;
    }

    private getFeatureIds(
        selectionCriteria: SelectionCriteria,
        filterLayers: Layer[],
        operationId: number
    ): Promise<string[]> {
        return this.getFeaturesOrFeatureIds(selectionCriteria, filterLayers, true, operationId) as Promise<string[]>;
    }

    private getFeaturesOrFeatureIds(
        selectionCriteria: SelectionCriteria,
        filterLayers: Layer[],
        returnFeatureIds: boolean,
        operationId: number
    ): Promise<string[] | Feature[]> {
        if (selectionCriteria != null) {
            this.showNotification();
        }

        const conditionallyConvertFeaturesToFeatureIds = (features: Feature[]): string[] | Feature[] =>
            returnFeatureIds ? features.map(feature => feature.id) : features;

        let promise: Promise<any>;

        if (this.isEmpty(selectionCriteria)) {
            promise = this.getResultFrom([]);
        } else if (this.isBounds(selectionCriteria)) {
            promise = this.getFeaturesByBounds(
                filterLayers,
                selectionCriteria as L.LatLngBounds | L.Polygon,
                operationId
            ).then(conditionallyConvertFeaturesToFeatureIds);
        } else if (this.isArrayOfFeatures(selectionCriteria)) {
            promise = this.getResultFrom(selectionCriteria).then(conditionallyConvertFeaturesToFeatureIds);
        } else if (this.isFeature(selectionCriteria)) {
            promise = this.getResultFrom([selectionCriteria]).then(conditionallyConvertFeaturesToFeatureIds);
        } else if (this.isArrayOfFeatureIds(selectionCriteria)) {
            promise = returnFeatureIds
                ? this.getResultFrom(selectionCriteria)
                : this.getFeaturesByFeatureIds(filterLayers, selectionCriteria as string[], operationId);
        } else if (this.isFeatureId(selectionCriteria)) {
            promise = returnFeatureIds
                ? this.getResultFrom([selectionCriteria])
                : this.getFeaturesByFeatureIds(filterLayers, [selectionCriteria as string], operationId);
        } else if (selectionCriteria instanceof TodoSelectionCriteria) {
            promise = this.getFeaturesByTodo(
                filterLayers,
                selectionCriteria as TodoSelectionCriteria,
                operationId
            ).then(conditionallyConvertFeaturesToFeatureIds);
        } else if (selectionCriteria instanceof TaskSelectionCriteria) {
            promise = this.getFeaturesByTask(
                filterLayers,
                selectionCriteria as TaskSelectionCriteria,
                operationId
            ).then(conditionallyConvertFeaturesToFeatureIds);
        } else if (selectionCriteria instanceof JobSelectionCriteria) {
            promise = this.getFeaturesByJob(filterLayers, selectionCriteria as JobSelectionCriteria, operationId).then(
                conditionallyConvertFeaturesToFeatureIds
            );
        } else if (selectionCriteria instanceof GeometryTypeSelectionCriteria) {
            promise = this.getFeaturesByGeometryType(
                selectionCriteria as GeometryTypeSelectionCriteria,
                operationId
            ).then(conditionallyConvertFeaturesToFeatureIds);
        } else {
            // Any other value is effectively "empty"
            promise = this.getResultFrom([]);
        }

        return promise.then(
            result => {
                this.clearNotification();
                return result;
            },
            (): string[] | Feature[] => []
        );
    }

    private isEmpty(selectionCriteria: SelectionCriteria): boolean {
        return selectionCriteria === null || selectionCriteria === undefined;
    }

    private isBounds(selectionCriteria: SelectionCriteria): boolean {
        return selectionCriteria instanceof L.LatLngBounds || selectionCriteria instanceof L.Polygon;
    }

    private isArrayOfFeatures(selectionCriteria: SelectionCriteria): boolean {
        return (
            ArrayUtils.isArray(selectionCriteria) &&
            ((selectionCriteria as any).length === 0 || (selectionCriteria as any)[0] instanceof Feature)
        );
    }

    private isArrayOfFeatureIds(selectionCriteria: SelectionCriteria): boolean {
        return (
            ArrayUtils.isArray(selectionCriteria) &&
            ((selectionCriteria as any).length === 0 || StringUtils.isString((selectionCriteria as any)[0]))
        );
    }

    private isFeature(selectionCriteria: SelectionCriteria): boolean {
        return selectionCriteria instanceof Feature;
    }

    private isFeatureId(selectionCriteria: SelectionCriteria): boolean {
        return StringUtils.isString(selectionCriteria);
    }

    private getFeaturesByBounds(
        filterLayers: Layer[],
        bounds: L.LatLngBounds | L.Polygon,
        operationId: number
    ): Promise<Feature[]> {
        return this.getFeaturesByBoundsAndOrFeatureIdsAndOrTodoIdsOrJobIdsAndOrGeometryType(
            filterLayers,
            bounds,
            null,
            null,
            null,
            null,
            operationId
        );
    }

    private getFeaturesByFeatureIds(
        filterLayers: Layer[],
        featureIds: string[],
        operationId: number
    ): Promise<Feature[]> {
        this.selectedFeatureIds = featureIds;
        return this.getFeaturesByBoundsAndOrFeatureIdsAndOrTodoIdsOrJobIdsAndOrGeometryType(
            null,
            null,
            null,
            null,
            null,
            null,
            operationId
        );
    }

    private getFeaturesByTodo(
        filterLayers: Layer[],
        todoSelectionCriteria: TodoSelectionCriteria,
        operationId: number
    ): Promise<Feature[]> {
        // Legacy Task workflow. Remove after migrating to new Task workflow.
        return this.getFeaturesByBoundsAndOrFeatureIdsAndOrTodoIdsOrJobIdsAndOrGeometryType(
            filterLayers,
            null,
            todoSelectionCriteria.featureIds,
            todoSelectionCriteria.todoIds,
            null,
            null,
            operationId
        );
    }

    private getFeaturesByJob(
        filterLayers: Layer[],
        jobSelectionCriteria: JobSelectionCriteria,
        operationId: number
    ): Promise<Feature[]> {
        return this.getFeaturesByBoundsAndOrFeatureIdsAndOrTodoIdsOrJobIdsAndOrGeometryType(
            filterLayers,
            null,
            jobSelectionCriteria.featureIds,
            null,
            jobSelectionCriteria.jobIds,
            null,
            operationId
        );
    }

    private getFeaturesByTask(
        filterLayers: Layer[],
        taskSelectionCriteria: TaskSelectionCriteria,
        operationId: number
    ): Promise<Feature[]> {
        return this.getFeaturesByBoundsAndOrFeatureIdsAndOrTodoIdsOrJobIdsAndOrGeometryType(
            filterLayers,
            null,
            taskSelectionCriteria.featureIds,
            taskSelectionCriteria.taskIds,
            null,
            null,
            operationId
        );
    }

    private getFeaturesByGeometryType(
        geometryTypeSelectionCriteria: GeometryTypeSelectionCriteria,
        operationId: number
    ): Promise<Feature[]> {
        return this.getFeaturesByBoundsAndOrFeatureIdsAndOrTodoIdsOrJobIdsAndOrGeometryType(
            geometryTypeSelectionCriteria.layers,
            null,
            null,
            null,
            null,
            geometryTypeSelectionCriteria.geometryType,
            operationId
        );
    }

    private async getFeaturesByBoundsAndOrFeatureIdsAndOrTodoIdsOrJobIdsAndOrGeometryType(
        filterLayers: Layer[],
        bounds: L.LatLngBounds | L.Polygon,
        featureIds: string[],
        todoIds: string[],
        jobIds: string[],
        geometryType: string,
        operationId: number
    ): Promise<Feature[]> {
        const promises: Promise<Feature[]>[] = [];

        const project = this.projectStream.currentProjectStream.getValue();
        filterLayers = filterLayers || [];
        todoIds = todoIds || [];
        jobIds = jobIds || [];

        if (!project || (filterLayers.length === 0 && todoIds.length === 0 && this.selectedFeatureIds.length === 0)) {
            return Promise.resolve([]);
        }

        let fullFeatureFilter: FullFeatureFilter;

        fullFeatureFilter = this.buildFullFeatureFilter(
            filterLayers,
            bounds,
            featureIds,
            todoIds,
            jobIds,
            geometryType
        );
        fullFeatureFilter.layers = fullFeatureFilter.layers || [];
        fullFeatureFilter.taskFeatures.taskIds = fullFeatureFilter.taskFeatures.taskIds || [];
        fullFeatureFilter.jobFeatures.jobIds = fullFeatureFilter.jobFeatures.jobIds || [];

        // Check if this operation has been superseded by a newer one.
        // Each operation checks if it's still the current operation. If not, cancel.
        const isOperationSuperseded = () => operationId !== this.currentOperationId;

        if (
            fullFeatureFilter.layers.length ||
            fullFeatureFilter.taskFeatures.taskIds.length ||
            fullFeatureFilter.jobFeatures.jobIds.length ||
            fullFeatureFilter.selectedFeatureIds.length
        ) {
            let accumulatedFeatures: Feature[] = [];

            this.isLoadingFeatures$.next(true);

            const getFeaturesPromise = this.featureService
                .getFeatures(
                    project.id,
                    fullFeatureFilter,
                    10000,
                    (pageFeatures: Feature[]) => {
                        if (isOperationSuperseded()) {
                            return;
                        }

                        accumulatedFeatures = [...accumulatedFeatures, ...pageFeatures];

                        this.currentFeatures = [...accumulatedFeatures];
                        this.currentFeaturesStream.next([...accumulatedFeatures]);
                    },
                    () => {
                        return isOperationSuperseded();
                    }
                )
                .then(features => {
                    if (isOperationSuperseded()) {
                        return [];
                    }

                    this.isLoadingFeatures$.next(false);
                    return features;
                });

            promises.push(getFeaturesPromise);
        }

        const layerFeatures = await Promise.all(promises);
        let allFeatures: Feature[] = [];

        if (isOperationSuperseded()) {
            return [];
        }

        layerFeatures.forEach(features => {
            allFeatures = allFeatures.concat(features);
        });

        return allFeatures;
    }

    private buildFullFeatureFilter(
        layers: Layer[],
        bounds: L.LatLngBounds | L.Polygon,
        featureIds: string[],
        todoIds: string[],
        jobIds: string[],
        geometryType: string
    ): FullFeatureFilter {
        let fullFeatureFilter = new FullFeatureFilter(this.featureFilter);

        if (layers) {
            fullFeatureFilter.layers = layers;
        }

        if (bounds) {
            fullFeatureFilter.bounds = bounds;
        }

        if (featureIds) {
            fullFeatureFilter.taskFeatures.featureIds = featureIds;
            fullFeatureFilter.layers = [];
        }

        if (todoIds.length) {
            fullFeatureFilter.taskFeatures.taskIds = todoIds;
            fullFeatureFilter.layers = [];
        }

        if (jobIds.length) {
            fullFeatureFilter.jobFeatures.jobIds = jobIds;
            fullFeatureFilter.layers = [];
        }

        if (geometryType) {
            fullFeatureFilter.geometryType = geometryType;
        }

        if (this.selectedFeatureIds.length) {
            fullFeatureFilter.selectedFeatureIds = this.selectedFeatureIds;
        }
        return fullFeatureFilter;
    }

    // return promise that resolves to the input item
    private getResultFrom(input: any): Promise<any> {
        return Promise.resolve(input);
    }

    showNotification(): void {
        if (!this.hideNotifications && !this.activeToast) {
            this.activeToast = this.messaging.showInfo(this.translate.instant('TC.Common.UpdatingSelection'), null, {
                positionClass: 'toast-top-right'
            });
        }
    }

    clearNotification(): void {
        if (this.activeToast && this.activeToast.toastId) {
            this.messaging.clear(this.activeToast.toastId);
            this.activeToast = null;
        }
    }

    // clear entire selection
    clear(callBack?: (features: Feature[]) => void): void {
        // Guard against recursive calls - if already clearing, return early.
        if (this.isCancelled && !this.currentFeatures?.length) {
            return;
        }

        // Cancel ongoing queries if any.
        this.isCancelled = true;
        this.currentOperationId++;

        this.isLoadingFeatures$.next(false);

        this.actionsQueue.complete();
        this.actionsQueue = new Subject<Observable<any[]>>();

        // Re-subscribe to the new queue
        this.actionsQueue
            .pipe(
                concatMap(identity),
                distinctUntilChanged(_.isEqual),
                tap((features: Feature[]) => {
                    this.currentFeatures = features;
                })
            )
            .subscribe(features => {
                this.currentFeaturesStream.next(features);
            });

        // Queue the empty array emission to ensure it goes through the subscription chain
        // This ensures FeaturesStreamsService receives it and closes the panel
        const clearAction = () => {
            this.currentFeatures = [];
            return Promise.resolve([]);
        };

        this.actionsQueue.next(defer(clearAction));

        // Also emit directly as a fallback to ensure immediate response
        this.currentFeatures = [];
        this.currentFeaturesStream.next([]);

        // Call callback if provided
        if (callBack) {
            callBack([]);
        }
    }
}
